import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star, Phone, MapPin, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedInventory from '../../../../components/RelatedInventory';

const LandRoverService = () => {
  const landRoverFeatures = [
    'JLR SDD Diagnostics',
    'British Luxury Experts',
    'Genuine Land Rover Parts',
    'Air Suspension Specialists',
    'Terrain Response Systems',
    'All Models & Years'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      title: 'Air Suspension Service',
      icon: Mountain,
      description: 'Expert air suspension diagnostics and repair:',
      items: [
        'Air spring replacement',
        'Compressor rebuilding',
        'Height sensor calibration',
        'Valve block repairs',
        'System leak detection'
      ]
    },
    {
      title: 'Engine & Transmission',
      icon: Settings,
      description: 'Complete powertrain services:',
      items: [
        'Ingenium engine specialists',
        'ZF transmission service',
        'Transfer case repairs',
        'Timing chain replacement',
        'Supercharger maintenance'
      ]
    },
    {
      title: 'Electrical Systems',
      icon: Car,
      description: 'Advanced electronic diagnostics:',
      items: [
        'Terrain Response calibration',
        'InControl Touch Pro repair',
        'Module programming',
        'Battery management systems',
        'Parking sensor diagnosis'
      ]
    },
    {
      title: 'Maintenance Services',
      icon: Wrench,
      description: 'Scheduled maintenance:',
      items: [
        'Premium oil changes',
        'Brake system service',
        'Differential service',
        'Coolant system flush',
        'Multi-point inspections'
      ]
    }
  ];

  const commonProblems = [
    {
      model: 'Range Rover (2013-2024)',
      issues: [
        'Air suspension compressor failure',
        'Crankshaft position sensor issues',
        'Coolant leaks from crossover pipes',
        'Infotainment system freezing',
        'Sunroof drain blockages'
      ]
    },
    {
      model: 'Discovery (2017-2024)',
      issues: [
        'DEF (AdBlue) system faults',
        'Transfer case bearing noise',
        'Electronic parking brake issues',
        'Door handle sensor failures',
        'Battery drain problems'
      ]
    },
    {
      model: 'Defender (2020-2024)',
      issues: [
        'Software glitches requiring updates',
        'Mild hybrid system faults',
        'Panel gaps and alignment',
        'Water ingress in door seals',
        'Camera system malfunctions'
      ]
    }
  ];

  const oilChangeWarnings = [
    {
      sign: 'Loud Engine Noises',
      description: 'Metal-to-metal contact from inadequate lubrication causes knocking, roaring, or rumbling sounds, especially on startup.',
      urgent: true
    },
    {
      sign: 'Dark, Dirty Oil',
      description: 'Oil that has turned dark brown or black and feels thick and gritty needs immediate replacement.',
      urgent: false
    },
    {
      sign: 'Burnt Oil Smell',
      description: 'A pungent burning odor indicates overheating or oil leaking onto hot engine components.',
      urgent: true
    },
    {
      sign: 'Exhaust Smoke',
      description: 'White, gray, or blue smoke indicates oil burning in the combustion chamber from worn piston rings.',
      urgent: true
    },
    {
      sign: 'Extended Service Interval',
      description: 'Oil degrades over time - change every 5,000-7,000 miles or 6 months, whichever comes first.',
      urgent: false
    }
  ];

  return (
    <>
      <Helmet>
        <title>Land Rover Repair Dallas | Range Rover Service Experts | Paradise-Performance</title>
        <meta name="description" content="Expert Land Rover & Range Rover repair in Dallas. Air suspension specialists, JLR diagnostics, genuine parts. Save 40% vs dealership. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/land-rover" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/land-rover" />
        <meta property="og:title" content="Land Rover & Range Rover Repair Dallas | Air Suspension Experts" />
        <meta property="og:description" content="Dallas Land Rover specialists. Air suspension, JLR diagnostics, all models. Save 40% vs dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/land-rover-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Land Rover & Range Rover Repair Dallas | Air Suspension Experts" />
        <meta name="twitter:description" content="Dallas Land Rover specialists. Air suspension, JLR diagnostics, all models. Save 40% vs dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/land-rover-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Land Rover Repair Dallas",
            "url": "https://paradise-performance.com/services/european-car-repair/land-rover",
            "description": "Expert Land Rover and Range Rover repair in Dallas. Specializing in air suspension, electrical systems, and scheduled maintenance.",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "45"
            }
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Land Rover maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Land Rover maintenance costs average $1,200 to $2,000 per year depending on the model. An oil change runs $150-$200 at an independent shop (vs $250-$400 at dealerships). Air suspension repairs range from $1,000-$3,500. At Paradise-Performance near Dallas, we're typically 30-40% less than Land Rover dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "Are Land Rovers expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Land Rovers are among the most expensive vehicles to maintain. Annual costs average $1,200-$2,000 for routine maintenance, but repairs can be costly—air suspension work runs $1,500-$4,000, transfer case repairs $2,000-$3,500. Using an independent specialist instead of the dealership saves 30-40%."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Land Rover problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common Land Rover issues include: air suspension failures (compressor, struts, valve block), coolant crossover pipe leaks, infotainment freezing, electrical gremlins, transfer case problems, and sunroof drain blockages. Texas heat accelerates cooling system and air suspension wear. Regular maintenance with JLR-spec fluids helps prevent issues."
                }
              },
              {
                "@type": "Question",
                "name": "How often should I service my Range Rover?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Range Rovers should be serviced every 10,000-15,000 miles or 12 months. Key intervals: oil change every 10,000 miles, brake fluid every 2 years, coolant every 5 years, air suspension inspection annually, and differential service every 30,000 miles. Dallas heat means more frequent cooling system checks."
                }
              },
              {
                "@type": "Question",
                "name": "Can air suspension be converted to coils on Land Rover?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, many Land Rover owners convert from air suspension to coil springs to eliminate expensive air suspension repairs. Conversion kits cost $1,500-$3,000 installed—often less than a single air strut replacement. You lose adjustable ride height but gain reliability. We offer both repair and conversion options."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Land Rover"
        brandColor="#005A2B"
        tagline="British Luxury SUV Specialists"
        description="From Range Rover elegance to Defender capability, our expert technicians keep your Land Rover performing at its peak through any terrain."
        features={landRoverFeatures}
        heroImage="/assets/assets/images/rangerov.webp"
        heroImageAlt="Land Rover Service Center Dallas"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Introduction */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Dallas' Premier Land Rover & Range Rover Specialists</h2>
            
            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg mb-8">
              <p className="text-lg font-semibold text-gray-800">
                <span className="text-red-600">⚠️ Did you know?</span> Over 70% of Land Rover owners experience electrical or drivetrain issues by 80,000 miles—many of which stem from improper maintenance or misdiagnosis.
              </p>
              <p className="text-gray-700 mt-2">
                Unlike generic shops, we focus exclusively on European auto repair in Dallas, ensuring your Land Rover gets the specialized care it deserves.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-lg text-gray-700 mb-4">
                  Land Rovers and Range Rovers represent the pinnacle of British luxury and off-road capability. 
                  These sophisticated vehicles require specialized knowledge and equipment that most shops simply 
                  don't possess. At Paradise-Performance, we combine professional JLR diagnostic tools with extensive 
                  of experience to deliver dealer-level service at independent shop prices.
                </p>
                <p className="text-lg text-gray-700">
                  Whether you're dealing with complex air suspension issues, electrical gremlins, or simply need 
                  routine maintenance, our expert technicians understand the intricacies of every Land Rover 
                  system and model.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Image Gallery Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative h-96 overflow-hidden rounded-lg shadow-2xl">
                <img 
                  src="/assets/assets/images/new/range-land-rover/b0b43409d9d7b6495201e1d8ea391d6c.webp"
                  alt="Land Rover specialist service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">British 4x4 Experts</h3>
                    <p className="text-white/90">Advanced diagnostics for all Land Rover models</p>
                  </div>
                </div>
              </div>
              <div className="relative h-96 overflow-hidden rounded-lg shadow-2xl">
                <img 
                  src="/assets/assets/images/new/range-land-rover/c28406e74179d4cc4fea0acad839aa00.webp"
                  alt="Range Rover air suspension repair"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Air Suspension Specialists</h3>
                    <p className="text-white/90">Expert repair for complex suspension systems</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose Us Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Why Dallas Land Rover Owners Choose Paradise-Performance</h3>
            <p className="text-lg text-gray-700 mb-8">
              Not all auto shops are equipped to handle Land Rover's sophisticated engineering. Here's why drivers trust us over dealerships and generic shops:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-[#005A2B]">
                <h4 className="text-xl font-bold text-[#005A2B] mb-4">1. Expert Land Rover Specialists</h4>
                <p className="text-gray-700 mb-3">
                  Our ASE-expert technicians undergo continuous training to master Land Rover's advanced systems:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Terrain Response® systems</strong> - Proper calibration for off-road performance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Ingenium engine diagnostics</strong> - Early detection of timing chain issues</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Air suspension repairs</strong> - Fix sagging or failure-prone components</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Electrical troubleshooting</strong> - Resolve battery drains and sensor issues</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-[#005A2B]">
                <h3 className="text-xl font-semibold mb-4">Why Choose Paradise-Performance?</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Save 30-40% compared to dealership pricing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Same-day service for most repairs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <span>12-month/12,000-mile warranty</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Expert technicians</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Land Rover Services</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div 
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg p-6"
                >
                  <div className="flex items-start mb-4">
                    <service.icon className="w-12 h-12 text-[#005A2B] mr-4 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">{service.title}</h3>
                      <p className="text-gray-700 mb-3">{service.description}</p>
                      <ul className="space-y-2">
                        {service.items.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Oil Change Warning Signs */}
          <motion.div {...fadeInUp} className="mb-16 bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5 Warning Signs Your Land Rover Needs an Oil Change</h2>
            <p className="text-lg text-gray-700 mb-8">
              Clean oil is essential for your Land Rover's performance and longevity. While manufacturers recommend 
              oil changes every 5,000-7,000 miles, these warning signs indicate immediate service is needed:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {oilChangeWarnings.map((warning, index) => (
                <div key={index} className={`border-l-4 ${warning.urgent ? 'border-red-500' : 'border-yellow-500'} pl-4`}>
                  <div className="flex items-start mb-2">
                    <AlertTriangle className={`w-6 h-6 ${warning.urgent ? 'text-red-500' : 'text-yellow-500'} mr-2 flex-shrink-0`} />
                    <h3 className="font-semibold text-lg">{warning.sign}</h3>
                  </div>
                  <p className="text-gray-700 text-sm">{warning.description}</p>
                  {warning.urgent && (
                    <p className="text-red-600 font-semibold text-sm mt-2">⚠️ Immediate attention required</p>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Our Premium Oil Change Service Includes:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Complete drainage of old oil</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Premium synthetic oil (0W-30 or 5W-30)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Genuine Land Rover oil filter</span>
                  </li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Multi-point inspection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Service indicator reset</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Fluid level top-offs</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Common Problems by Model */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Common Land Rover Issues We Fix</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {commonProblems.map((model, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-[#005A2B]">
                  <h3 className="text-xl font-semibold mb-4 text-[#005A2B]">{model.model}</h3>
                  <ul className="space-y-2">
                    {model.issues.map((issue, idx) => (
                      <li key={idx} className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Air Suspension Expertise */}
          <motion.div {...fadeInUp} className="mb-16 bg-gradient-to-r from-[#005A2B] to-[#007A3D] text-white rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-6">Air Suspension Specialists</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="mb-4">
                  Land Rover's air suspension system provides unmatched ride quality and off-road capability, 
                  but it requires specialized expertise when problems arise. Our technicians are extensively 
                  trained in diagnosing and repairing all air suspension components.
                </p>
                <p className="mb-4">
                  Common symptoms we address:
                </p>
                <ul className="space-y-2 mb-4">
                  <li>• Vehicle sagging overnight</li>
                  <li>• "Suspension Fault" warning messages</li>
                  <li>• Compressor running constantly</li>
                  <li>• Uneven ride height</li>
                  <li>• Harsh ride quality</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Our Air Suspension Services:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Shield className="h-6 w-6 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Diagnostic Testing:</strong> Complete system scan with SDD to identify faults
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Shield className="h-6 w-6 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Air Spring Replacement:</strong> OEM-quality springs with 2-year warranty
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Shield className="h-6 w-6 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Compressor Service:</strong> Rebuild or replacement with genuine parts
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Shield className="h-6 w-6 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Calibration:</strong> Proper height sensor adjustment and system programming
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* All Models Serviced */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">All Land Rover & Range Rover Models Serviced</h2>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#005A2B] mb-3">Range Rover</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Range Rover</li>
                  <li>• Range Rover Sport</li>
                  <li>• Range Rover Velar</li>
                  <li>• Range Rover Evoque</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#005A2B] mb-3">Discovery</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Discovery</li>
                  <li>• Discovery Sport</li>
                  <li>• LR3/LR4</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#005A2B] mb-3">Defender</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Defender 90</li>
                  <li>• Defender 110</li>
                  <li>• Defender 130</li>
                  <li>• Classic Defender</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#005A2B] mb-3">Classic Models</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Freelander</li>
                  <li>• LR2</li>
                  <li>• Series I-III</li>
                  <li>• P38 Range Rover</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gray-900 text-white rounded-xl p-12">
            <Mountain className="w-16 h-16 mx-auto mb-6 text-[#005A2B]" />
            <h2 className="text-3xl font-bold mb-4">Schedule Your Land Rover Service Today</h2>
            <p className="text-xl mb-8 text-gray-300">
              Expert British luxury SUV service with significant savings vs. the dealership
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:8173822316" 
                className="inline-block px-8 py-4 bg-[#005A2B] text-white font-bold rounded-lg hover:bg-[#003D1F] transition-colors"
              >
                <Phone className="inline-block w-5 h-5 mr-2" />
                Call (817) 382-2316
              </a>
              <Link 
                to="/contact" 
                className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Online
              </Link>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-gray-400">
                <MapPin className="inline-block w-4 h-4 mr-1" />
                305 Co Rd 3470, Paradise, TX 76073 • Serving all of Dallas-Fort Worth
              </p>
            </div>
          </motion.div>

          {/* Related Services */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Also servicing other British brands: {' '}
              <Link to="/services/european-car-repair/jaguar" className="text-[#005A2B] hover:underline">Jaguar</Link>, {' '}
              <Link to="/services/european-car-repair/mini" className="text-[#005A2B] hover:underline">MINI</Link>, {' '}
              <Link to="/services/european-car-repair/bentley" className="text-[#005A2B] hover:underline">Bentley</Link>, {' '}
              <Link to="/services/european-car-repair/aston-martin" className="text-[#005A2B] hover:underline">Aston Martin</Link>, {' '}
              and all <Link to="/services/european-car-repair" className="text-[#005A2B] hover:underline">European auto repair Dallas</Link> services
            </p>
          </div>
        </div>
      </div>

      {/* Land Rover Vehicles For Sale */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory make="Land Rover" limit={4} title="Land Rover Vehicles For Sale" />
      </div>
    </>
  );
};

export default LandRoverService;
